import { useEffect } from 'react';
import { Button, Flex, Text, Title, Icon } from '@nula/core-ui';
import { NulaLockup } from '@/components/nula-lockup/nula-lockup';
import styles from './auth-desktop.module.css';

/**
 * `/auth/desktop` — desktop OAuth handoff page.
 *
 * The system browser lands here after Auth0 sign-in on the desktop build. Its
 * only job is to forward `?code&state` to `nula://callback`, handing control
 * back to the running Tauri app (which holds the PKCE verifier and performs the
 * token exchange — see apps/lumen/src/app/routes/login.tsx). It then renders a
 * quiet Nula confirmation so the user sees a real page instead of the
 * provider's redirect spinner.
 *
 * This page is intentionally STATELESS: no token exchange, no sessionStorage,
 * no auth store, no platform branch. It is unconditionally the deep-link
 * courier — correct by construction. The web flow uses `/auth/callback`, which
 * completes the exchange in-page; the two never share behaviour.
 *
 * The browser tab cannot auto-close (browsers refuse window.close() on tabs the
 * user navigated to), so the page tells the user they can close it and offers a
 * "Return to Nula" button that re-fires the deep link if the OS prompt was
 * dismissed.
 */

type BounceTarget = { kind: 'home' } | { kind: 'deeplink'; url: string };

/**
 * Decide where the page should navigate, given the URL query string. Pure so it
 * can be unit-tested without touching window.location.
 */
export function desktopBounceTarget(search: string): BounceTarget {
  if (!search || search === '?') return { kind: 'home' };
  return { kind: 'deeplink', url: `nula://callback${search}` };
}

/** Navigation seam — injected in tests, defaults to real window.location. */
export interface DesktopNav {
  search: string;
  go: (url: string) => void;
}

function defaultNav(): DesktopNav {
  return {
    search: window.location.search,
    go: (url) => window.location.replace(url),
  };
}

export function AuthDesktop({ nav }: { nav?: DesktopNav }) {
  const resolved = nav ?? defaultNav();
  const target = desktopBounceTarget(resolved.search);

  useEffect(() => {
    if (target.kind === 'home') {
      resolved.go('/');
    } else {
      resolved.go(target.url);
    }
    // Run once on mount — the search string is fixed for the page's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reopen = () => {
    if (target.kind === 'deeplink') resolved.go(target.url);
  };

  return (
    <Flex align="center" justify="center" className={styles.root}>
      <Flex vertical align="center" className={styles.card}>
        <NulaLockup height={40} className={styles.lockup} />
        <Flex align="center" justify="center" className={styles.seal}>
          <Icon name="check" size={22} weight={400} />
        </Flex>
        <Title level={1} className={styles.title}>
          You’re signed in
        </Title>
        <Text className={styles.message}>
          Nula is opening. Head back to the app to pick up where you left off.
        </Text>
        <Button
          variant="default"
          icon={<Icon name="open_in_new" size={16} weight={300} />}
          className={styles.action}
          onClick={reopen}
        >
          Return to Nula
        </Button>
        <Text className={styles.hint}>You can close this tab.</Text>
      </Flex>
    </Flex>
  );
}
